'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Heading, HStack, Text, VStack } from '@jaaq/hub-ui';
import { ArrowLeft, CodeXml, Monitor } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { ExplainerModal } from '#components/explainer-modal';
import { OnboardingShell } from '#components/onboarding-shell';
import { useOnboardingState } from '#lib/onboarding-provider';
import { computeRecommendedVariant } from '#lib/recommendations/compute-recommended-variant';
import { getReasonText } from '#lib/recommendations/get-reason-text';
import type { Variant } from '#lib/steps/state';
import { RecommendationCard } from './recommendation-card';
const EXPLAINER_VIDEO_SRC: Record<Variant, string> = {
  hosted: '/hub_hosted_explainer.mp4',
  embedded: '/hub_embedded_explainer.mp4'
};
export const RecommendationStep = () => {
  const $ = _c(44);
  const {
    onboardingState,
    patch,
    goNext,
    goBack
  } = useOnboardingState();
  const {
    outcomes,
    digitalSetups,
    priority
  } = onboardingState;
  const [explainerVariant, setExplainerVariant] = useState(null);
  let computedVariant;
  let t0;
  if ($[0] !== digitalSetups || $[1] !== outcomes || $[2] !== priority) {
    computedVariant = computeRecommendedVariant(outcomes, digitalSetups, priority);
    t0 = getReasonText(outcomes, digitalSetups, priority, computedVariant);
    $[0] = digitalSetups;
    $[1] = outcomes;
    $[2] = priority;
    $[3] = computedVariant;
    $[4] = t0;
  } else {
    computedVariant = $[3];
    t0 = $[4];
  }
  const reason = t0;
  let t1;
  if ($[5] !== goNext || $[6] !== patch) {
    t1 = function choose(recommendedVariant) {
      patch({
        recommendedVariant
      });
      goNext();
    };
    $[5] = goNext;
    $[6] = patch;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const choose = t1;
  const t2 = computedVariant === "hosted" ? "Hosted" : "Embedded";
  let t3;
  if ($[8] !== t2) {
    t3 = <Heading level={1} variant="display" tone="inverse">We recommend JAAQ {t2}</Heading>;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== reason) {
    t4 = <Text variant="body-lg" tone="inverse" muted={true} className="max-w-160">Based on your answers, especially because {reason}. But you can still choose either option below.</Text>;
    $[10] = reason;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <VStack gap={3}>{t3}{t4}</VStack>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = ["Custom branding", "Full user management", "Analytics dashboard"];
    t7 = <Monitor className="size-6 text-fg" />;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  const t8 = computedVariant === "hosted";
  let t9;
  if ($[17] !== choose) {
    t9 = () => choose("hosted");
    $[17] = choose;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setExplainerVariant("hosted");
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t8 || $[21] !== t9) {
    t11 = <RecommendationCard title="Hosted" tagline="A dedicated, branded JAAQ experience at your own subdomain" bullets={t6} icon={t7} isRecommended={t8} onChoose={t9} onExplore={t10} />;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = ["Complete customization", "Seamless user experience", "Full API access"];
    t13 = <CodeXml className="size-6 text-fg" />;
    $[23] = t12;
    $[24] = t13;
  } else {
    t12 = $[23];
    t13 = $[24];
  }
  const t14 = computedVariant === "embedded";
  let t15;
  if ($[25] !== choose) {
    t15 = () => choose("embedded");
    $[25] = choose;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setExplainerVariant("embedded");
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t14 || $[29] !== t15) {
    t17 = <RecommendationCard title="Embedded" tagline="Integrate JAAQ directly into your app or website via SDK/API" bullets={t12} icon={t13} isRecommended={t14} onChoose={t15} onExplore={t16} />;
    $[28] = t14;
    $[29] = t15;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t11 || $[32] !== t17) {
    t18 = <div className="bg-jaaq-grey-600 rounded-3xl p-5"><HStack gap={2.5}>{t11}{t17}</HStack></div>;
    $[31] = t11;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ArrowLeft className="size-4" strokeWidth={1.5} />;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== goBack) {
    t20 = <div><Button variant="outlined" size="x-large" onClick={goBack}>{t19}Back</Button></div>;
    $[35] = goBack;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== explainerVariant) {
    t21 = explainerVariant && <ExplainerModal variant={explainerVariant} onClose={() => setExplainerVariant(null)} videoSrc={EXPLAINER_VIDEO_SRC[explainerVariant]} />;
    $[37] = explainerVariant;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t18 || $[40] !== t20 || $[41] !== t21 || $[42] !== t5) {
    t22 = <OnboardingShell showActions={false} contentMaxWidth="1000px">{t5}{t18}{t20}{t21}</OnboardingShell>;
    $[39] = t18;
    $[40] = t20;
    $[41] = t21;
    $[42] = t5;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  return t22;
};